import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { toast } from "sonner";
import { Gift, Plus, Settings as SettingsIcon, Trash2, History, Search, IndianRupee, Save, Image as ImageIcon, Sparkles, Loader2, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const LoyaltyPage = () => {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "ADMIN", "SALES_ADMIN"].includes(user?.role);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [tab, setTab] = useState("gifts");

  return (
    <div className="p-6 space-y-6" data-testid="loyalty-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Gift className="text-[#8B1A1A]" /> Loyalty & Gifting
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Manage points engine, gift catalogue, and per-dealer redemption history.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="gifts" data-testid="loyalty-tab-gifts">
            <Gift size={14} className="mr-1.5" /> Gifts
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="loyalty-tab-settings" disabled={!isAdmin}>
            <SettingsIcon size={14} className="mr-1.5" /> Settings {!isAdmin && "(admin)"}
          </TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="loyalty-tab-redemptions">
            <History size={14} className="mr-1.5" /> Redemptions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gifts" className="mt-4">
          <GiftsTab isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          {isAdmin ? <SettingsTab isSuperAdmin={isSuperAdmin} /> : <Card className="p-6 text-slate-500">Admin access required.</Card>}
        </TabsContent>
        <TabsContent value="redemptions" className="mt-4">
          <RedemptionsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ─────────────────────── Settings Tab ───────────────────────

function SettingsTab({ isSuperAdmin }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await authFetch(`${API}/loyalty/settings`);
    const j = await r.json();
    if (j.success) setSettings(j.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/loyalty/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      let j = {};
      try { j = await r.clone().json(); }
      catch { try { const t = await r.text(); j = t ? JSON.parse(t) : {}; } catch { j = {}; } }
      if (r.ok && (j.success === undefined || j.success)) { toast.success("Settings saved"); if (j.data) setSettings(j.data); }
      else toast.error(j.error?.message || "Save failed");
    } catch (e) { toast.error(e?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-slate-500 p-6">Loading settings…</div>;
  const rupeePerPoint = Number(settings.rupee_value_per_point || 0);
  const bagsForOnePoint = settings.points_per_bag ? (1 / settings.points_per_bag).toFixed(2) : "—";
  const rupeesPer100 = (rupeePerPoint * 100).toFixed(2);

  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="p-5 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
        <div className="text-sm text-amber-900 dark:text-amber-200">
          <b>Admin only.</b> The rupee value of a point is never shown to dealers or employees — only points totals are.
          Current setup: <b>1 point = ₹{rupeePerPoint.toFixed(2)}</b>, <b>{settings.points_per_bag} point(s)/bag</b>
          ({bagsForOnePoint} bag = 1 point · 100 points = ₹{rupeesPer100}).
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h3 className="font-semibold text-slate-900 dark:text-white">Points engine</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Rupee value per point (admin-only)</label>
            <div className="relative mt-1">
              <IndianRupee size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="number" step="0.01" min="0.01" className="pl-7"
                value={settings.rupee_value_per_point}
                onChange={(e) => setSettings({ ...settings, rupee_value_per_point: parseFloat(e.target.value || 0) })}
                data-testid="settings-rupee-per-point"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Points per bag</label>
            <Input
              type="number" min="0" step="1" className="mt-1"
              value={settings.points_per_bag}
              onChange={(e) => setSettings({ ...settings, points_per_bag: parseFloat(e.target.value || 0) })}
              data-testid="settings-points-per-bag"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Minimum points to redeem</label>
            <Input
              type="number" min="0" step="1" className="mt-1"
              value={settings.min_redeem_points}
              onChange={(e) => setSettings({ ...settings, min_redeem_points: parseInt(e.target.value || 0) })}
              data-testid="settings-min-redeem"
            />
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Terms & Conditions</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Shown on the gift catalogue and printed on redemption letters.</p>
        <Textarea
          rows={10}
          value={settings.terms_and_conditions || ""}
          onChange={(e) => setSettings({ ...settings, terms_and_conditions: e.target.value })}
          className="font-mono text-xs"
          data-testid="settings-tnc"
        />
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !isSuperAdmin} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="settings-save-btn">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
      {!isSuperAdmin && <div className="text-xs text-slate-500 text-right">Only Super Admin can edit settings.</div>}
    </div>
  );
}

// ─────────────────────── Gifts Tab ───────────────────────

function GiftsTab({ isAdmin }) {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ rupee_value_per_point: 0.20 });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = isAdmin ? `${API}/v1/loyalty/gifts/admin` : `${API}/v1/loyalty/gifts`;
      const r = await authFetch(url);
      const j = await r.json();
      if (j.success) setGifts(j.data || []);
      if (isAdmin) {
        const sr = await authFetch(`${API}/loyalty/settings`);
        const sj = await sr.json();
        if (sj.success) setSettings(sj.data);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isAdmin]);

  const editorOpen = (g) => {
    setEditing(g || { name: "", description: "", category: "", image_url: "", rupee_price: "", points_required: "", stock: 0, terms_and_conditions: "", active: true });
    setOpen(true);
  };

  const save = async () => {
    if (!editing.name) return toast.error("Name is required");
    const body = { ...editing };
    // Strip empty-string price fields so backend's "either rupee_price OR
    // points_required" logic works correctly (iter78 fix).
    for (const k of ["rupee_price", "points_required"]) {
      if (body[k] === "" || body[k] == null) delete body[k];
      else body[k] = k === "rupee_price" ? parseFloat(body[k]) : parseInt(body[k]);
    }
    if (body.rupee_price == null && body.points_required == null) {
      return toast.error("Set rupee_price or points_required");
    }
    body.stock = parseInt(body.stock || 0);
    const url = editing.gift_id ? `${API}/loyalty/gifts/${editing.gift_id}` : `${API}/loyalty/gifts`;
    const method = editing.gift_id ? "PUT" : "POST";
    try {
      const r = await authFetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      let j = {};
      try { j = await r.clone().json(); }
      catch { try { const t = await r.text(); j = t ? JSON.parse(t) : {}; } catch { j = {}; } }
      if (r.ok && (j.success === undefined || j.success)) {
        toast.success(editing.gift_id ? "Gift updated" : "Gift created");
        setOpen(false);
        load();
      } else {
        toast.error(j.error?.message || j.detail || `Save failed (HTTP ${r.status})`);
      }
    } catch (e) {
      toast.error(e?.message || "Save failed");
    }
  };

  const del = async (g) => {
    if (!confirm(`Delete "${g.name}"?`)) return;
    try {
      const r = await authFetch(`${API}/loyalty/gifts/${g.gift_id}`, { method: "DELETE" });
      let j = {};
      try { j = await r.clone().json(); }
      catch { try { const t = await r.text(); j = t ? JSON.parse(t) : {}; } catch { j = {}; } }
      if (r.ok && (j.success === undefined || j.success)) { toast.success("Gift deleted"); load(); }
      else toast.error(j.error?.message || "Delete failed");
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  };

  // live points calc from rupee_price
  const livePoints = editing && editing.rupee_price && settings.rupee_value_per_point
    ? Math.round(parseFloat(editing.rupee_price) / settings.rupee_value_per_point)
    : null;

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end gap-2">
          <Button onClick={() => setAiOpen(true)} variant="secondary" className="bg-white border-2 border-violet-300 text-violet-700 hover:bg-violet-50 dark:bg-violet-900/30 dark:border-violet-700 dark:text-violet-200 font-medium" data-testid="ai-suggest-btn">
            <Sparkles size={14} className="mr-1.5" /> AI Suggest gifts
          </Button>
          <Button onClick={() => editorOpen(null)} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="add-gift-btn">
            <Plus size={14} className="mr-1.5" /> New gift
          </Button>
        </div>
      )}
      <AiSuggestDialog open={aiOpen} onClose={() => setAiOpen(false)} onDone={load} settings={settings} isAdmin={isAdmin} />

      {loading ? (
        <div className="text-slate-500 p-6">Loading gifts…</div>
      ) : gifts.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="gifts-empty-state">
          <Gift size={48} className="mx-auto mb-2 text-slate-300" />
          No gifts yet. Click <b>+ New gift</b> to add the first one.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {gifts.map((g) => (
            <Card key={g.gift_id} className="overflow-hidden" data-testid={`gift-card-${g.gift_id}`}>
              <div className="aspect-video bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                {g.image_url ? <img src={g.image_url} alt={g.name} className="w-full h-full object-cover" /> : <ImageIcon size={32} className="text-slate-300" />}
              </div>
              <div className="p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-slate-900 dark:text-white">{g.name}</h4>
                  {!g.active && <Badge variant="outline" className="text-[10px]">Paused</Badge>}
                </div>
                <p className="text-xs text-slate-500 line-clamp-2 min-h-[32px]">{g.description}</p>
                <div className="flex items-center gap-2 text-xs">
                  <Badge className="bg-violet-100 text-violet-700 border-violet-200">{g.points_required} points</Badge>
                  {isAdmin && g.rupee_price_internal != null && (
                    <span className="text-slate-500" title="Admin only">₹{g.rupee_price_internal}</span>
                  )}
                  <span className="ml-auto text-slate-400">Stock: {g.stock ?? 0}</span>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 pt-2">
                    <Button size="sm" variant="outline" onClick={() => editorOpen(g)} data-testid={`edit-gift-${g.gift_id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(g)} data-testid={`del-gift-${g.gift_id}`}><Trash2 size={14} className="text-red-500" /></Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="gift-editor">
          <DialogHeader>
            <DialogTitle>{editing?.gift_id ? "Edit gift" : "New gift"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold">Name *</label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="gift-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold">Description</label>
                <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="gift-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold">Category</label>
                  <Input value={editing.category} placeholder="e.g. Electronics" onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold">Stock</label>
                  <Input type="number" min="0" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="gift-stock-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold">Cost in ₹ (admin-only)</label>
                  <Input
                    type="number" min="0" step="0.01" value={editing.rupee_price}
                    onChange={(e) => setEditing({ ...editing, rupee_price: e.target.value, points_required: "" })}
                    placeholder="e.g. 200"
                    data-testid="gift-rupee-input"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    {livePoints != null ? <>≈ <b>{livePoints} points</b> at ₹{settings.rupee_value_per_point}/point</> : "We'll convert this to points automatically."}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold">…OR set points directly</label>
                  <Input
                    type="number" min="0" value={editing.points_required}
                    onChange={(e) => setEditing({ ...editing, points_required: e.target.value, rupee_price: "" })}
                    placeholder="e.g. 1000"
                    data-testid="gift-points-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold">Image URL</label>
                <Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://…" data-testid="gift-image-input" />
                {editing.image_url && <img src={editing.image_url} alt="preview" className="mt-2 max-h-32 rounded border" />}
              </div>
              <div>
                <label className="text-xs font-semibold">Terms & Conditions (specific to this gift)</label>
                <Textarea rows={3} value={editing.terms_and_conditions} onChange={(e) => setEditing({ ...editing, terms_and_conditions: e.target.value })} placeholder="Delivery time, restrictions, etc." data-testid="gift-tnc-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="gift-active-switch" />
                <span className="text-sm">{editing.active ? "Active (visible to dealers)" : "Paused"}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} className="bg-white border-2 border-slate-300 text-slate-700">Cancel</Button>
            <Button onClick={save} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="gift-save-btn">
              {editing?.gift_id ? "Save changes" : "Create gift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────── Redemptions Tab ───────────────────────

function RedemptionsTab() {
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState("");
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    authFetch(`${API}/parties?limit=100`)
      .then((r) => r.json())
      .then((j) => setDealers(j.data || j || []))
      .catch(() => {});
  }, []);

  const load = async (id) => {
    if (!id) return;
    setDealerId(id);
    setLoading(true);
    try {
      const r = await authFetch(`${API}/loyalty/redemptions/by-dealer/${id}`);
      const j = await r.json();
      if (j.success) setHistory({ items: j.data || [], meta: j.meta || {} });
      else toast.error(j.error?.message || "Could not load history");
    } finally { setLoading(false); }
  };

  const filtered = dealers.filter((d) => {
    if (!search) return true;
    const hay = [d.firm_name, d.party_name, d.name, d.contact_person, d.party_id].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  const dealerLabel = (d) => d.firm_name || d.party_name || d.name || d.contact_person || (d.party_id || d.customer_id || d.id);

  const selectedDealer = dealers.find((d) => (d.party_id || d.customer_id || d.id) === dealerId);

  return (
    <div className="grid grid-cols-12 gap-4">
      <Card className="col-span-4 p-3 max-h-[70vh] overflow-y-auto">
        <div className="relative mb-2">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-7" placeholder="Search dealer…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="redemption-dealer-search" />
        </div>
        {filtered.length === 0 ? (
          <div className="text-slate-500 text-sm p-4 text-center">No dealers</div>
        ) : (
          <div className="space-y-1">
            {filtered.map((d) => {
              const id = d.party_id || d.customer_id || d.id;
              return (
                <button
                  key={id}
                  onClick={() => load(id)}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm ${dealerId === id ? "bg-[#8B1A1A] text-white" : "hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"}`}
                  data-testid={`dealer-pick-${id}`}
                >
                  {dealerLabel(d)}
                  <div className="text-[10px] opacity-70">{id}</div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="col-span-8 p-4 min-h-[70vh]" data-testid="redemption-history-panel">
        {!dealerId ? (
          <div className="text-slate-500 text-center p-8">Pick a dealer on the left to see their redemption history.</div>
        ) : loading ? (
          <div className="text-slate-500 p-6">Loading…</div>
        ) : !history?.items?.length ? (
          <div className="text-slate-500 text-center p-8">No redemptions for this dealer yet.</div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b pb-3 mb-3">
              <div>
                <div className="text-xs text-slate-500">Dealer</div>
                <div className="font-semibold text-slate-900 dark:text-white">{selectedDealer ? dealerLabel(selectedDealer) : dealerId}</div>
                <div className="text-[10px] text-slate-400 font-mono">{dealerId}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Total points redeemed</div>
                <div className="font-bold text-2xl text-[#8B1A1A]">{history.meta.total_points_redeemed || 0}</div>
                <div className="text-[11px] text-slate-400">{history.meta.count || history.items.length} redemption(s)</div>
              </div>
            </div>
            <div className="space-y-2">
              {history.items.map((r) => (
                <div key={r.redemption_id || r.id || r.created_at} className="flex items-center justify-between p-3 rounded border dark:border-slate-700">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{r.gift_name || r.item_name || "Gift"}</div>
                    <div className="text-xs text-slate-500">{r.created_at?.slice(0, 19).replace("T", " ")} · {r.status || "PENDING"}</div>
                    {r.delivery_address && <div className="text-[11px] text-slate-400 mt-0.5">→ {r.delivery_address}</div>}
                  </div>
                  <Badge className="bg-violet-100 text-violet-700 border-violet-200">{r.points_used || r.points_spent || 0} pts</Badge>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default LoyaltyPage;

// ─────────────────────── AI Suggest Dialog ───────────────────────

function AiSuggestDialog({ open, onClose, onDone, settings, isAdmin }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState({}); // {idx: true}
  const [creating, setCreating] = useState(false);

  const generate = async () => {
    setLoading(true);
    setItems(null);
    try {
      const r = await authFetch(`${API}/loyalty/gifts/ai-suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tiers: [50, 100, 250, 500, 1000, 2500] }),
      });
      let j = {};
      try { j = await r.clone().json(); }
      catch { try { const t = await r.text(); j = t ? JSON.parse(t) : {}; } catch { j = {}; } }
      if (r.ok && (j.success === undefined || j.success)) {
        setItems(j.data || []);
        // Pre-select everything by default
        const initial = {};
        (j.data || []).forEach((_, i) => { initial[i] = true; });
        setPicked(initial);
      } else {
        toast.error(j.error?.message || j.detail || "AI suggest failed");
      }
    } catch (e) {
      toast.error(e?.message || "AI suggest failed");
    } finally {
      setLoading(false);
    }
  };

  const bulkCreate = async () => {
    if (!items) return;
    const chosen = items.filter((_, i) => picked[i]);
    if (chosen.length === 0) return toast.error("Pick at least one gift to add");
    setCreating(true);
    let ok = 0;
    for (const g of chosen) {
      try {
        const r = await authFetch(`${API}/loyalty/gifts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: g.name,
            description: g.description,
            category: g.category,
            points_required: g.points_required,
            stock: 0,
            active: true,
          }),
        });
        if (r.ok) ok += 1;
      } catch { /* ignore single failures */ }
    }
    setCreating(false);
    toast.success(`Added ${ok} of ${chosen.length} gifts`);
    onClose();
    setItems(null);
    setPicked({});
    onDone?.();
  };

  const totalPoints = items
    ? items.filter((_, i) => picked[i]).reduce((s, g) => s + (g.points_required || 0), 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="ai-suggest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-violet-600" /> AI gift catalogue suggester
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Get 6 ready-to-add gift ideas at common point tiers (50 / 100 / 250 / 500 / 1000 / 2500 pts). Review, untick what you don't want, then add the rest with one click.
          </p>
          {!items ? (
            <div className="flex justify-center py-6">
              <Button onClick={generate} disabled={loading} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-suggest-generate-btn">
                {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Generating…</> : <><Sparkles size={14} className="mr-1.5" /> Generate suggestions</>}
              </Button>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {items.map((g, i) => (
                <label key={i} className="flex items-start gap-2 p-2.5 rounded border dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" data-testid={`ai-item-${i}`}>
                  <input
                    type="checkbox"
                    checked={!!picked[i]}
                    onChange={(e) => setPicked({ ...picked, [i]: e.target.checked })}
                    className="mt-1"
                    data-testid={`ai-item-check-${i}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold text-sm">{g.name}</span>
                      <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">{g.points_required} pts</Badge>
                      {isAdmin && <span className="text-[11px] text-slate-400">≈ ₹{g.rupee_price_internal}</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{g.description}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{g.category}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} className="bg-white border-2 border-slate-300 text-slate-700">
            <X size={14} className="mr-1" /> Close
          </Button>
          {items && (
            <Button onClick={bulkCreate} disabled={creating || totalPoints === 0} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="ai-suggest-bulk-create-btn">
              {creating ? <><Loader2 size={14} className="animate-spin mr-2" /> Adding…</> : <><Plus size={14} className="mr-1.5" /> Add {Object.values(picked).filter(Boolean).length} to catalogue</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
